/**
 * GalleryPage — Unified media library view
 * Shows all user photos from media_library in one place.
 * "Dodajesz zdjęcia raz — system używa ich wszędzie."
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, Trash2, Loader2, Plus, Camera, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PanelLayout from '@/components/PanelLayout';
import ContextPanel from '@/components/ContextPanel';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/utils/api';
import { getImageUrl } from '@/utils/imageUrl';
import { useTranslation } from 'react-i18next';

const SOURCE_LABELS = {
  profile: 'Z profilu',
  upload: 'Wgrane',
  generated: 'Wygenerowane',
  imported: 'Zaimportowane',
  kennel: 'Hodowla',
};

export default function GalleryPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [migrated, setMigrated] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isHodowla = (user?.profile_type || 'hodowla') !== 'biznes';

  const fetchMedia = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/media-library`, { headers });
      const mediaItems = res.data.items || [];
      
      // Auto-migrate if library is empty (first visit)
      if (mediaItems.length === 0 && !migrated) {
        setMigrated(true);
        try {
          const migRes = await axios.post(`${API}/media-library/migrate`, {}, { headers });
          if (migRes.data.migrated > 0) {
            const freshRes = await axios.get(`${API}/media-library`, { headers });
            setItems(freshRes.data.items || []);
            setLoading(false);
            return;
          }
        } catch { /* migration failed, continue with empty */ }
      }
      
      setItems(mediaItems);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API}/media-library/upload`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        setItems(prev => [res.data, ...prev]);
      } catch (err) { console.error('Upload error:', err); }
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/media-library/${id}`, { headers });
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) { console.error(e); }
    finally { setDeleting(null); }
  };

  const totalCount = items.length;

  return (
    <PanelLayout>
      <div className="max-w-[1200px] mx-auto" data-testid="gallery-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold text-zinc-900 mb-1">Twoje zdjęcia</h1>
          <p className="text-[15px] text-zinc-500">
            {totalCount > 0
              ? `Masz ${totalCount} ${totalCount === 1 ? 'zdjęcie' : totalCount < 5 ? 'zdjęcia' : 'zdjęć'} — dodajesz raz, używamy ich wszędzie`
              : isHodowla
                ? 'Dodaj zdjęcia hodowli, psów i szczeniąt'
                : 'Dodaj zdjęcia firmy, usług i realizacji'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* CENTER COLUMN */}
          <div className="lg:col-span-8" data-testid="gallery-content">
            {loading && (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
              </div>
            )}

            {/* Empty State */}
            {!loading && totalCount === 0 && (
              <div className="bg-white rounded-2xl border border-zinc-200 p-10 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="empty-gallery">
                <div className="w-14 h-14 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center mx-auto mb-5">
                  <Camera className="w-7 h-7 text-[#F47A3B]" />
                </div>
                <h2 className="text-[18px] font-bold text-zinc-900 mb-2">
                  Dodaj zdjęcia i zwiększ zainteresowanie swoją ofertą
                </h2>
                <p className="text-[14px] text-zinc-500 leading-relaxed mb-5 max-w-md mx-auto">
                  Dodaj zdjęcia raz — użyjemy ich na stronie, w promocji i odpowiedziach do klientów
                </p>
                <div className="relative inline-block">
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                  <Button className="bg-[#F47A3B] hover:bg-[#E06A2B] rounded-xl px-6" disabled={uploading} data-testid="empty-upload-btn">
                    {uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wgrywanie...</> : <><Upload className="w-4 h-4 mr-2" /> Dodaj pierwsze zdjęcia</>}
                  </Button>
                </div>
              </div>
            )}

            {/* Gallery Grid */}
            {!loading && totalCount > 0 && (
              <>
                <div className="flex justify-end mb-4">
                  <div className="relative">
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="upload-input" />
                    <Button className="bg-[#F47A3B] hover:bg-[#E06A2B] rounded-xl" disabled={uploading} data-testid="upload-btn">
                      {uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wgrywanie...</> : <><Plus className="w-4 h-4 mr-2" /> Dodaj zdjęcia</>}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="gallery-grid">
                  {/* Upload card */}
                  <div className="relative aspect-square rounded-2xl border-2 border-dashed border-zinc-200 bg-white flex items-center justify-center hover:border-[#F47A3B]/30 transition cursor-pointer group">
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                    <div className="text-center">
                      <Plus className="w-8 h-8 text-zinc-300 group-hover:text-[#F47A3B] mx-auto mb-2 transition" />
                      <span className="text-xs text-zinc-400 group-hover:text-[#F47A3B] transition">Dodaj</span>
                    </div>
                  </div>

                  {items.map(item => (
                    <div key={item.id} className="relative aspect-square rounded-2xl overflow-hidden bg-[#F3F4F6] group border border-zinc-200" data-testid={`gallery-item-${item.id}`}>
                      <img src={getImageUrl(item.file_url || item.thumbnail_url)} alt={item.alt_text || item.title} className="w-full h-full object-cover" loading="lazy" />
                      {/* Delete button */}
                      <button
                        onClick={() => handleDelete(item.id)}
                        disabled={deleting === item.id}
                        className="absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-red-600 rounded-full text-white flex items-center justify-center transition-colors z-10"
                        data-testid={`delete-${item.id}`}
                      >
                        {deleting === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                      {/* Source label */}
                      {item.source && item.source !== 'upload' && (
                        <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/40 rounded-full">
                          <span className="text-[10px] text-white font-medium">{SOURCE_LABELS[item.source] || item.source}</span>
                        </div>
                      )}
                      {/* Tags */}
                      {item.usage_tags && item.usage_tags.length > 0 && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 flex items-center gap-1 flex-wrap">
                          {item.usage_tags.slice(0, 3).map(tag => (
                            <span key={tag} className="text-[9px] text-white/80 bg-white/15 px-1.5 py-0.5 rounded">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* RIGHT COLUMN */}
          <ContextPanel
            title="Co Ci to daje"
            desc="Dodajesz zdjęcia raz — używamy ich na stronie, w promocji i odpowiedziach"
            benefits={[
              'Jedno miejsce na wszystkie zdjęcia',
              'System automatycznie dobiera zdjęcia do postów',
              'Klienci widzą Twoje zdjęcia wszędzie',
            ]}
            action={totalCount > 0
              ? 'Masz już zdjęcia — dodaj więcej, aby mieć większy wybór'
              : 'Dodaj zdjęcia, żebyśmy mogli ich używać w promocji'}
            link={{ to: '/panel/profil?tab=photos', title: 'Zarządzaj zdjęciami w profilu', subtitle: 'Profil = główne źródło zdjęć' }}
            icon={Camera}
          />
        </div>
      </div>
    </PanelLayout>
  );
}
